import type {
  ProductFacetValueDto,
  ProductForm,
  ProductMaterial,
} from '@up2stay/up2promo-api-client';

import {
  INTENDED_BEHAVIOR_OPTIONS,
  UNKNOWN_FACET_VALUE,
  type ProductKindLabels,
} from './products.constants';

export type ProductsListFilterValues = {
  behaviors: string[];
  forms: string[];
  materials: string[];
  colors: string[];
  sizes: string[];
  languages: string[];
  locationId: string | null;
};

export const EMPTY_PRODUCTS_LIST_FILTERS: ProductsListFilterValues = {
  behaviors: [],
  forms: [],
  materials: [],
  colors: [],
  sizes: [],
  languages: [],
  locationId: null,
};

/** Read `?locationId=` from Products list search (location detail CTAs). */
export function parseProductsLocationIdFromSearch(
  search: string,
): string | null {
  const id = new URLSearchParams(search).get('locationId')?.trim();
  return id || null;
}

/** Keep other query keys; set or clear `locationId` for URL sync. */
export function buildProductsListSearch(
  currentSearch: string,
  locationId: string | null,
): string {
  const params = new URLSearchParams(currentSearch);
  if (locationId) {
    params.set('locationId', locationId);
  } else {
    params.delete('locationId');
  }
  return params.toString();
}

export function toggleInList(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export function countActiveFilters(value: ProductsListFilterValues): number {
  return (
    value.behaviors.length +
    value.forms.length +
    value.materials.length +
    value.colors.length +
    value.sizes.length +
    value.languages.length +
    (value.locationId ? 1 : 0)
  );
}

/** Facet buckets with hits; a missing value becomes {@link UNKNOWN_FACET_VALUE}. */
export function facetOptions(
  facets: ProductFacetValueDto[] | undefined,
): Array<{ value: string; count: number }> {
  return (facets ?? [])
    .filter((f) => f.count > 0)
    .map((f) => ({
      value: f.value ?? UNKNOWN_FACET_VALUE,
      count: f.count,
    }));
}

/**
 * A facet section is worth showing only when it offers a real choice: two
 * or more values (e.g. "PVC" and "Unknown"), or a value the user already
 * picked so it stays visible and can be cleared (DEV-556).
 */
export function facetHasChoice(
  options: Array<{ value: string; count: number }>,
  selected: string[],
): boolean {
  return selected.length > 0 || options.length >= 2;
}

export function labelForFacetValue(
  value: string,
  knownLabels: Record<string, string> | undefined,
  unknownLabel: string,
): string {
  if (value === UNKNOWN_FACET_VALUE) return unknownLabel;
  return knownLabels?.[value] ?? value;
}

export type ProductsListFiltersLabels = {
  filter: string;
  filtersTitle: string;
  clearAll: string;
  done: string;
  sectionBehaviour: string;
  sectionForm: string;
  sectionMaterial: string;
  sectionColor: string;
  sectionSize: string;
  sectionLocale: string;
  sectionLocation: string;
  allLocations: string;
  noLocations: string;
  unknown: string;
  behaviourLabels: Record<string, string>;
  formLabels: Record<ProductForm, string>;
  materialLabels: Record<ProductMaterial, string>;
};

export type LocationFilterOption = { id: string; label: string };

export function buildBehaviourLabelMap(
  edit: ProductKindLabels,
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const opt of INTENDED_BEHAVIOR_OPTIONS) {
    map[opt.value] = edit[opt.labelKey] ?? opt.value;
  }
  return map;
}
